import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useGolfApi } from '../services/golfApi';
import { ArrowLeft, Trophy, Medal, TrendingUp, TrendingDown, Users, Target, Zap } from 'lucide-react';

const GolfLeaderboardPage: React.FC = () => {
  const { outingId } = useParams<{ outingId: string }>();
  const navigate = useNavigate();
  const golfApi = useGolfApi();
  const [autoRefresh, setAutoRefresh] = useState(true);

  const { data: leaderboardData, isLoading, refetch } = useQuery({
    queryKey: ['golf', 'outing', outingId, 'leaderboard'],
    queryFn: () => golfApi.getLeaderboard(outingId!),
    refetchInterval: autoRefresh ? 10000 : false, // Auto-refresh every 10 seconds
  });

  const { data: sideGames = [] } = useQuery({
    queryKey: ['golf', 'side-games', outingId],
    queryFn: () => golfApi.getSideGames(outingId!),
    enabled: !!outingId,
  });

  if (isLoading || !leaderboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const { leaderboard, course, outing } = leaderboardData;

  const getMedalIcon = (position: number) => {
    if (position === 1) return <Medal className="h-6 w-6 text-yellow-500" />;
    if (position === 2) return <Medal className="h-6 w-6 text-gray-400" />;
    if (position === 3) return <Medal className="h-6 w-6 text-amber-600" />;
    return null;
  };

  const formatToPar = (toPar: number) => {
    if (toPar === 0) return 'E';
    return toPar > 0 ? `+${toPar}` : toPar.toString();
  };

  const getScoreColorClass = (toPar: number) => {
    if (toPar < 0) return 'text-blue-600 dark:text-blue-400';
    if (toPar > 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-900 dark:text-white';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <button
            onClick={() => navigate(`/golf/outings/${outingId}`)}
            className="flex items-center gap-2 text-green-100 hover:text-white mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Outing
          </button>

          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">{outing.name}</h1>
              <p className="text-green-100 flex items-center gap-4">
                <span>{course.name}</span>
                <span>•</span>
                <span>Par {course.totalPar}</span>
                <span>•</span>
                <span>{course.totalYardage?.toLocaleString()} yards</span>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                Auto-refresh
              </label>
              {outing.status === 'in-progress' && (
                <button
                  onClick={() => navigate(`/golf/outings/${outingId}/scorecard`)}
                  className="px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-green-50 font-semibold"
                >
                  Enter Scores
                </button>
              )}
            </div>
          </div>

          {/* Live Status */}
          {outing.status === 'in-progress' && (
            <div className="flex items-center gap-2 text-green-100">
              <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
              <span className="text-sm">Live Updates</span>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Leaderboard */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Trophy className="h-6 w-6 text-green-600" />
              Leaderboard
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Holes
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    To Par
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {leaderboard.map((team, index) => (
                  <tr
                    key={team.teamId}
                    className={`${
                      index === 0 ? 'bg-yellow-50 dark:bg-yellow-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    } transition-colors`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getMedalIcon(index + 1) || (
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-6 text-center">
                            {index + 1}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 dark:text-white mb-1">
                        {team.teamName}
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {team.players.length} player{team.players.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {team.holesCompleted}/18
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        {team.totalScore || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={`text-lg font-bold ${getScoreColorClass(team.toPar)}`}>
                          {formatToPar(team.toPar)}
                        </span>
                        {team.toPar < 0 && <TrendingDown className="h-4 w-4 text-blue-600" />}
                        {team.toPar > 0 && <TrendingUp className="h-4 w-4 text-red-600" />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => {
                          // Could expand to show hole-by-hole breakdown
                        }}
                        className="text-sm text-green-600 hover:text-green-700 dark:text-green-400 font-medium"
                      >
                        View Card →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Side Games */}
        {sideGames.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Target className="h-6 w-6 text-green-600" />
                Side Games
              </h2>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sideGames.map((game, index) => (
                <div
                  key={index}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  <div className="flex items-center gap-2 mb-3">
                    {game.gameType === 'closest-to-pin' && <Target className="h-5 w-5 text-blue-600" />}
                    {game.gameType === 'longest-drive' && <Zap className="h-5 w-5 text-yellow-600" />}
                    {game.gameType === 'skin' && <Trophy className="h-5 w-5 text-green-600" />}
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white capitalize">
                        {game.gameType.replace('-', ' ')}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Hole {game.hole}
                      </div>
                    </div>
                  </div>

                  {game.winners && game.winners.length > 0 ? (
                    <div className="space-y-2">
                      {game.winners.map((winner, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-900 dark:text-white flex items-center gap-2">
                            <Medal className="h-4 w-4 text-yellow-500" />
                            {winner.userId}
                          </span>
                          {winner.value && (
                            <span className="text-gray-600 dark:text-gray-400">
                              {game.gameType === 'closest-to-pin' && `${winner.value}"`}
                              {game.gameType === 'longest-drive' && `${winner.value} yds`}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      No winner yet
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hole-by-Hole Scorecard Preview */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Hole-by-Hole Scores
            </h2>
          </div>

          <div className="p-6 overflow-x-auto">
            {/* Front 9 */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Front 9</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <th className="text-left py-2 pr-4">Team</th>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(h => (
                      <th key={h} className="text-center py-2 px-2 min-w-[30px]">{h}</th>
                    ))}
                    <th className="text-center py-2 pl-4 font-bold">OUT</th>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400">
                    <td className="py-2 pr-4 text-xs">Par</td>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(h => (
                      <td key={h} className="text-center py-2 px-2">
                        {course.holes.find(hole => hole.holeNumber === h)?.par}
                      </td>
                    ))}
                    <td className="text-center py-2 pl-4 font-semibold">
                      {course.holes.filter(h => h.holeNumber <= 9).reduce((sum, h) => sum + h.par, 0)}
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.slice(0, 5).map((team) => (
                    <tr key={team.teamId} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white truncate max-w-[150px]">
                        {team.teamName}
                      </td>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(h => (
                        <td key={h} className="text-center py-2 px-2">
                          {team.holes[h] ? (
                            <span className={`font-semibold ${
                              team.holes[h] < (course.holes.find(hole => hole.holeNumber === h)?.par || 4)
                                ? 'text-blue-600'
                                : team.holes[h] > (course.holes.find(hole => hole.holeNumber === h)?.par || 4)
                                ? 'text-red-600'
                                : 'text-gray-900 dark:text-white'
                            }`}>
                              {team.holes[h]}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      ))}
                      <td className="text-center py-2 pl-4 font-bold">
                        {Object.entries(team.holes)
                          .filter(([hole]) => parseInt(hole) <= 9)
                          .reduce((sum, [, score]) => sum + score, 0) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Back 9 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Back 9</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                    <th className="text-left py-2 pr-4">Team</th>
                    {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => (
                      <th key={h} className="text-center py-2 px-2 min-w-[30px]">{h}</th>
                    ))}
                    <th className="text-center py-2 pl-4 font-bold">IN</th>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400">
                    <td className="py-2 pr-4 text-xs">Par</td>
                    {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => (
                      <td key={h} className="text-center py-2 px-2">
                        {course.holes.find(hole => hole.holeNumber === h)?.par}
                      </td>
                    ))}
                    <td className="text-center py-2 pl-4 font-semibold">
                      {course.holes.filter(h => h.holeNumber > 9).reduce((sum, h) => sum + h.par, 0)}
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.slice(0, 5).map((team) => (
                    <tr key={team.teamId} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white truncate max-w-[150px]">
                        {team.teamName}
                      </td>
                      {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => (
                        <td key={h} className="text-center py-2 px-2">
                          {team.holes[h] ? (
                            <span className={`font-semibold ${
                              team.holes[h] < (course.holes.find(hole => hole.holeNumber === h)?.par || 4)
                                ? 'text-blue-600'
                                : team.holes[h] > (course.holes.find(hole => hole.holeNumber === h)?.par || 4)
                                ? 'text-red-600'
                                : 'text-gray-900 dark:text-white'
                            }`}>
                              {team.holes[h]}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      ))}
                      <td className="text-center py-2 pl-4 font-bold">
                        {Object.entries(team.holes)
                          .filter(([hole]) => parseInt(hole) > 9)
                          .reduce((sum, [, score]) => sum + score, 0) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Refresh Info */}
        <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {autoRefresh && 'Automatically refreshing every 10 seconds'}
          {!autoRefresh && (
            <button onClick={() => refetch()} className="text-green-600 hover:text-green-700">
              Click to refresh manually
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GolfLeaderboardPage;

